<template>
  <div class="relative mx-auto max-w-[var(--breakpoint-2xl)]">
    <section class="w-full space-y-6">
      <MarketingTabsProductsActivitiesFilters
        :locations="locations"
        @update:search="searchQuery = $event"
        @update:location="updateLocation"
        @update:type="updateType"
        @update:indoor-outdoor="updateIndoorOutdoor"
        @update:experience="updateExperience"
      />
      <div
        v-if="!isLoading && sortedActivities.length === 0"
        class="text-center font-medium text-neutral-400"
      >
        There are currently no activities available at
        {{ blok.headline }}
        <span
          v-if="
            searchQuery !== '' ||
            selectedType !== 'all' ||
            selectedAge !== 'all' ||
            selectedIndoorOutdoor !== 'all'
          "
        >
          for these filters</span
        >.
      </div>

      <div
        class="grid grid-cols-2 items-stretch gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4"
      >
        <LazyMarketingCardActivity
          v-for="(activity, index) in sortedActivities"
          v-if="!isLoading"
          :key="index"
          :activity="activity"
          :blok="blok"
          :type="type"
          :name="name"
        />
        <LazyMarketingCardActivityLoading
          v-for="i in 4"
          v-if="isLoading"
          :key="i"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { ISbStoryData } from '@storyblok/vue';
import type { LocationStoryblok } from '~/types/component-types-sb';

const { fetchActivities, fetchLocations, isLoading } = useStoryblokClient();

defineProps<{
  blok: Record<string, any>;
  uuid: string;
  type: string;
  name?: string;
}>();

const activities = ref<any[]>([]);
const locations = ref<ISbStoryData<LocationStoryblok>[]>([]);

// Filter state
const searchQuery = ref('');
const selectedLocation = ref('all');
const selectedType = ref('all');
const selectedAge = ref('all');
const selectedIndoorOutdoor = ref('all');
const selectedExperience = ref('all');

// Load activities and set unique locations with activity count
const loadActivities = async () => {
  const [stories, locationData] = await Promise.all([
    fetchActivities(),
    fetchLocations(),
  ]);
  activities.value = stories;
  locations.value =
    locationData as unknown as ISbStoryData<LocationStoryblok>[];
};

await loadActivities();

// Filter functions
const updateLocation = (location: string) => {
  selectedLocation.value = location;
};

const updateType = (type: string) => {
  selectedType.value = type;
};

const updateIndoorOutdoor = (type: string) => {
  selectedIndoorOutdoor.value = type;
};

const updateExperience = (experience: string) => {
  selectedExperience.value = experience;
};

// Computed properties for filtered activities
const filteredActivities = computed(() => {
  return activities.value.filter((activity) => {
    // Search query filter
    if (searchQuery.value) {
      const searchTerm = searchQuery.value.toLowerCase();
      const activityName = activity.name?.toLowerCase() || '';
      const metaTitle = activity.content?.meta_title?.toLowerCase() || '';
      const metaDescription =
        activity.content?.meta_description?.toLowerCase() || '';
      const keywords =
        activity.content?.meta_keywords?.value?.map((k: string) =>
          k.toLowerCase()
        ) || [];
      const activityTypes =
        activity.content?.activity_types?.map((t: string) => t.toLowerCase()) ||
        [];

      // Check if any field matches
      const hasMatch =
        activityName.includes(searchTerm) ||
        metaTitle.includes(searchTerm) ||
        metaDescription.includes(searchTerm) ||
        keywords.some((keyword: string) => keyword.includes(searchTerm)) ||
        activityTypes.some((type: string) => type.includes(searchTerm));

      if (!hasMatch) {
        return false;
      }
    }

    // Location filter
    if (selectedLocation.value !== 'all') {
      const hasLocation = activity.content?.locations?.some(
        (location: ISbStoryData<LocationStoryblok>) => {
          return location.name === selectedLocation.value;
        }
      );
      if (!hasLocation) return false;
    }

    // Type filter
    if (selectedType.value !== 'all') {
      const hasType = activity.content?.activity_types?.includes(
        selectedType.value
      );
      if (!hasType) return false;
    }

    // Age filter
    if (selectedAge.value !== 'all') {
      const hasAge = activity.content?.age_groups?.includes(selectedAge.value);
      if (!hasAge) return false;
    }

    // Indoor/Outdoor filter
    if (selectedIndoorOutdoor.value !== 'all') {
      if (
        !activity.content?.activity_indoor_outdoor?.includes(
          selectedIndoorOutdoor.value
        )
      ) {
        return false;
      }
    }

    // Experience filter
    if (selectedExperience.value !== 'all') {
      const hasExperience = activity.content?.experience_levels?.includes(
        selectedExperience.value
      );
      if (!hasExperience) return false;
    }

    return true;
  });
});

// Computed properties for sorted activities

// Helper function to get price and handle NaN
const getPrice = (item: any): number => {
  const price = item.content?.products?.[0]?.price;
  const numPrice = Number(price);
  return !isNaN(numPrice) && typeof numPrice === 'number' ? numPrice : 0;
};

// Helper function to check if an item is corporate
const isCorporate = (item: any): boolean => {
  const corporateType = 'corporate';
  const types = item.content?.activity_types;
  return Array.isArray(types) && types.includes(corporateType);
};

// Sort non-corporate items: Price Desc -> Name Asc
const sortItems = (a: any, b: any): number => {
  const priceA = getPrice(a);
  const priceB = getPrice(b);
  const priceOrder = priceB - priceA;
  if (priceOrder !== 0) return priceOrder;

  return (a.name ?? '').localeCompare(b.name ?? '');
};

const sortedActivities = computed(() => {
  const nonCorporate = filteredActivities.value.filter(
    (item) => !isCorporate(item)
  );
  const corporate = filteredActivities.value.filter((item) =>
    isCorporate(item)
  );

  const sortedNonCorporate = [...nonCorporate].sort(sortItems);
  const sortedCorporate = [...corporate].sort(sortItems);

  return [...sortedNonCorporate, ...sortedCorporate];
});
</script>
